import React, { createContext, useContext, useReducer, useMemo } from 'react';
import { useFeedbackToolQueries } from './useFeedbackTool';

const FeedbackToolContext = createContext(null);

const defaultFilterParams = {
  years: [],
  subjects: [],
  taskTypes: [],
  responseTypes: [],
  state: undefined,
  sortOrder: undefined,
};

const initialState = {
  selectedItems: new Set(),
  queryParams: { ...defaultFilterParams },
  context: 'markingCriteria',
};

function feedbackToolReducer(state, action) {
  switch (action.type) {
    case 'SET_SELECTED_ITEMS':
      const newState = { ...state, selectedItems: new Set(action.payload) };
      return newState;
    case 'SET_QUERY_PARAMS':
      return { ...state, queryParams: { ...state.queryParams, ...action.payload } };
    case 'SET_CONTEXT':
      return { ...state, context: action.payload };
    case 'RESET':
      return { ...initialState, queryParams: state.queryParams, context: state.context };
    default:
      return state;
  }
}

export function FeedbackToolProvider({ 
  children, 
  initialQueryParams = {}, 
  context = 'markingCriteria'
}) {
  const [state, dispatch] = useReducer(feedbackToolReducer, {
    ...initialState,
    queryParams: { ...defaultFilterParams, ...initialQueryParams },
    context,
  });

  const { queries, mutations } = useFeedbackToolQueries(
    state.queryParams, 
    state.context
  );

  const contextValue = useMemo(() => ({
    state,
    dispatch,
    queries,
    mutations,
    queryParams: state.queryParams,
    selectedItems: state.selectedItems,
    selectedItemsSize: state.selectedItems.size,
    selectItem: (id) => {
      const newSelected = new Set(state.selectedItems);
      newSelected.add(id);
      dispatch({ type: 'SET_SELECTED_ITEMS', payload: Array.from(newSelected) });
    },
    selectAll: (ids) => {
      dispatch({ type: 'SET_SELECTED_ITEMS', payload: ids });
    },
    deselectAll: () => {
      dispatch({ type: 'SET_SELECTED_ITEMS', payload: [] });
    },
    setSearchValue: (value) => {
      dispatch({ type: 'SET_QUERY_PARAMS', payload: { search: value } });
    },
    setShowHidden: (value) => {
      dispatch({ type: 'SET_QUERY_PARAMS', payload: { showHidden: value } });
    },
    setShowOnlyMineFilter: (value) => {
      dispatch({ type: 'SET_QUERY_PARAMS', payload: { showOnlyMineFilter: value } });
    },
    setFilterParams: (payload) => {
      dispatch({ type: 'SET_QUERY_PARAMS', payload });
    },
    enableReactQuery: true,
  }), [state, queries, mutations]);

  return (
    <FeedbackToolContext.Provider value={contextValue}>
      {children}
    </FeedbackToolContext.Provider>
  );
}

export function useFeedbackToolContext() {
  const context = useContext(FeedbackToolContext);
  if (!context) {
    throw new Error('useFeedbackToolContext must be used within a FeedbackToolProvider');
  }
  return context;
} 